import React, { useState } from 'react';

const AudioStemMixer = ({
    stems,
    onGainChange,
    settings,
    onSettingsChange,
    className = ''
}) => {
    const [isExpanded, setIsExpanded] = useState(false);

    const handleVolumeChange = (stemId, value) => {
        const gain = parseFloat(value);
        onGainChange(stemId, gain);

        // Update settings for persistence
        const settingKey = `${stemId}Volume`;
        if (onSettingsChange) {
            onSettingsChange({
                ...settings,
                [settingKey]: gain
            });
        }
    };

    const getStemIcon = (stemType) => {
        switch (stemType) {
            case 'mantra': return '🎵';
            case 'ambient': return '🌊';
            case 'bell': return '🔔';
            default: return '🎶';
        }
    };

    const getStemColor = (stemType) => {
        switch (stemType) {
            case 'mantra': return 'bg-purple-600';
            case 'ambient': return 'bg-blue-600';
            case 'bell': return 'bg-yellow-600';
            default: return 'bg-gray-600';
        }
    };

    if (!stems || stems.length === 0) return null;

    return (
        <div className={`${className}`}>
            {/* Toggle Button */}
            <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="bg-gray-900 bg-opacity-75 hover:bg-opacity-90 px-3 py-2 rounded-lg text-white transition-all mb-2 flex items-center gap-2"
                title="Audio Mixer"
            >
                🎛️ <span className="text-sm">Mix</span>
                <span className={`transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}>
                    ▼
                </span>
            </button>

            {/* Mixer Panel */}
            {isExpanded && (
                <div className="bg-gray-900 bg-opacity-90 rounded-lg p-4 min-w-64 backdrop-blur-sm">
                    <h3 className="text-white text-sm font-semibold mb-3">Audio Stems</h3>

                    {/* Individual Stem Controls */}
                    <div className="space-y-3">
                        {stems.map((stem) => {
                            const currentVolume = settings[`${stem.id}Volume`] ?? stem.gain ?? 0.5;

                            return (
                                <div key={stem.id} className="flex items-center gap-3">
                                    {/* Stem Icon */}
                                    <div
                                        className={`w-6 h-6 rounded flex items-center justify-center text-xs ${getStemColor(stem.type)}`}
                                        title={stem.type}
                                    >
                                        {getStemIcon(stem.type)}
                                    </div>

                                    {/* Stem Label */}
                                    <div className="flex-shrink-0 w-16">
                                        <span className="text-white text-xs capitalize">
                                            {stem.id}
                                        </span>
                                    </div>

                                    {/* Volume Slider */}
                                    <div className="flex-1">
                                        <input
                                            type="range"
                                            min="0"
                                            max="1"
                                            step="0.01"
                                            value={currentVolume}
                                            onChange={(e) => handleVolumeChange(stem.id, e.target.value)}
                                            className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
                                            style={{
                                                background: `linear-gradient(to right, ${getStemColor(stem.type)} 0%, ${getStemColor(stem.type)} ${currentVolume * 100}%, #374151 ${currentVolume * 100}%, #374151 100%)`
                                            }}
                                        />
                                    </div>

                                    {/* Volume Value */}
                                    <div className="flex-shrink-0 w-8">
                                        <span className="text-gray-300 text-xs">
                                            {Math.round(currentVolume * 100)}
                                        </span>
                                    </div>

                                    {/* Mute Button */}
                                    <button
                                        onClick={() => handleVolumeChange(stem.id, currentVolume > 0 ? 0 : stem.gain ?? 0.5)}
                                        className="w-6 h-6 flex items-center justify-center text-xs hover:bg-gray-700 rounded transition-colors"
                                        title={currentVolume > 0 ? 'Mute' : 'Unmute'}
                                    >
                                        {currentVolume > 0 ? '🔊' : '🔇'}
                                    </button>
                                </div>
                            );
                        })}
                    </div>

                    {/* Master Controls */}
                    <div className="border-t border-gray-700 mt-4 pt-3">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-white text-xs font-semibold">Master</span>
                            <button
                                onClick={() => {
                                    // Mute all stems
                                    stems.forEach(stem => handleVolumeChange(stem.id, 0));
                                }}
                                className="text-xs text-gray-400 hover:text-white transition-colors"
                            >
                                Mute All
                            </button>
                        </div>

                        {/* Quick Presets */}
                        <div className="flex gap-2 mt-2">
                            <button
                                onClick={() => {
                                    // Meditation preset: lower volumes
                                    stems.forEach(stem => {
                                        const preset = stem.type === 'mantra' ? 0.6 :
                                                     stem.type === 'ambient' ? 0.3 :
                                                     0.1;
                                        handleVolumeChange(stem.id, preset);
                                    });
                                }}
                                className="text-xs bg-blue-600 bg-opacity-50 hover:bg-opacity-70 px-2 py-1 rounded transition-colors text-white"
                            >
                                Soft
                            </button>
                            <button
                                onClick={() => {
                                    // Balanced preset
                                    stems.forEach(stem => {
                                        const preset = stem.gain ?? 0.5;
                                        handleVolumeChange(stem.id, preset);
                                    });
                                }}
                                className="text-xs bg-green-600 bg-opacity-50 hover:bg-opacity-70 px-2 py-1 rounded transition-colors text-white"
                            >
                                Reset
                            </button>
                            <button
                                onClick={() => {
                                    // Immersive preset: higher volumes
                                    stems.forEach(stem => {
                                        const preset = stem.type === 'mantra' ? 1.0 :
                                                     stem.type === 'ambient' ? 0.7 :
                                                     0.9;
                                        handleVolumeChange(stem.id, preset);
                                    });
                                }}
                                className="text-xs bg-red-600 bg-opacity-50 hover:bg-opacity-70 px-2 py-1 rounded transition-colors text-white"
                            >
                                Full
                            </button>
                        </div>
                    </div>

                    {/* Loop Controls (for stems that support it) */}
                    <div className="border-t border-gray-700 mt-3 pt-3">
                        <div className="text-xs text-gray-400 mb-2">Loop Settings</div>
                        <div className="flex flex-wrap gap-2">
                            {stems
                                .filter(stem => stem.loop)
                                .map(stem => (
                                    <button
                                        key={`loop-${stem.id}`}
                                        className="text-xs bg-gray-700 hover:bg-gray-600 px-2 py-1 rounded transition-colors text-white"
                                        onClick={() => {
                                            // TODO: Implement loop count control
                                            console.log(`Loop control for ${stem.id}`);
                                        }}
                                    >
                                        {stem.id} ∞
                                    </button>
                                ))
                            }
                        </div>
                    </div>
                </div>
            )}

            {/* Custom CSS for slider styling */}
            <style jsx>{`
                .slider::-webkit-slider-thumb {
                    appearance: none;
                    height: 16px;
                    width: 16px;
                    border-radius: 50%;
                    background: #ffffff;
                    cursor: pointer;
                    border: 2px solid #4b5563;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.3);
                }

                .slider::-moz-range-thumb {
                    height: 16px;
                    width: 16px;
                    border-radius: 50%;
                    background: #ffffff;
                    cursor: pointer;
                    border: 2px solid #4b5563;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.3);
                }
            `}</style>
        </div>
    );
};

export default AudioStemMixer;